import React, { FC, ReactElement, useContext, useMemo } from 'react'

import { MsalProvider } from '@azure/msal-react'
import { BrowserCacheLocation, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser'

import { Branding, BrandingConfigSSOButton } from '../../../../React/Common/API/BrandingAPI'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { useFeatureFlag } from '../../../../React/Common/Hooks/useFeatureFlag'
import { SignInWith as SignInWithLanguage } from '../../../../../i18n'
import { BroadworksSSO } from './BroadworksSSO'
import { StateSetter } from '../../../../React/Common/Types/Types'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { configureFullURLForLocalDevelopment } from '../../../../React/Common/Utils/LocalUtils'
import { MicrosoftSSO } from './MicrosoftSSO'
import { useMicrosoftSSOConfig } from '../../../../React/Common/Hooks/useMicrosoftSSOConfig'
import { MicrosoftSSOConfigDTO } from '../../../../NonReact/Types/Types'
import { getAppV2APIBasePath } from '../../../../NonReact/constants/ApiConstants'
import { RouteID } from '../Header/Types/Types'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { LandingPageProps } from './Types/Types'

type Props = {
    branding: Nullable<Branding>
    setBroadworksSSORegEx: StateSetter<Nullable<RegExp>>
    isBroadworksSignInMode: boolean
    setIsLoadingServices: StateSetter<boolean>
    isLoadingServices: boolean
    landingPage?: LandingPageProps
    resetForm?: () => void
}

export const SignInWith: FC<Props> = ({
    branding,
    setBroadworksSSORegEx,
    isBroadworksSignInMode,
    setIsLoadingServices,
    isLoadingServices,
    landingPage,
    resetForm
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(SignInWithLanguage.NAME)

    const broadworksSSOEnabled: Definable<boolean> = useFeatureFlag('enableBroadworksSelfSignUp')

    const microsoftSSOConfig: Nullable<MicrosoftSSOConfigDTO> = useMicrosoftSSOConfig()
    const msalInstance: Nullable<IPublicClientApplication> = useMemo((): Nullable<IPublicClientApplication> => {
        if (!microsoftSSOConfig?.auth) return null

        return new PublicClientApplication({
            auth: {
                clientId: microsoftSSOConfig.auth.clientId,
                authority: microsoftSSOConfig.auth.authority,
                redirectUri: getAppV2APIBasePath().appendPathParams(RouteID.AUTH).constructURL()
            },
            cache: {
                cacheLocation: BrowserCacheLocation.MemoryStorage
            }
        })
    }, [microsoftSSOConfig])

    return !ArrayUtils.isEmpty(branding?.BrandingConfig?.ssoButtons) || broadworksSSOEnabled || microsoftSSOConfig ? (
        <div className='router-sign-in-with'>
            {!isBroadworksSignInMode ? (
                <h4 className='rsiw-title'>
                    <span>{language.get(SignInWithLanguage.SignInWith_Title)}</span>
                </h4>
            ) : (
                <></>
            )}

            <div className='rsiw-buttons'>
                <BroadworksSSO
                    branding={branding}
                    bwSSOEnabled={!!broadworksSSOEnabled}
                    setBroadworksSSORegEx={setBroadworksSSORegEx}
                    isBroadworksSignInMode={isBroadworksSignInMode}
                    isDisabled={isLoadingServices}
                />
                {isBroadworksSignInMode ? (
                    <></>
                ) : (
                    branding?.BrandingConfig?.ssoButtons?.map(
                        ({ icon, label, url }: BrandingConfigSSOButton): ReactElement => (
                            <RoundedButton
                                disabled={isLoadingServices}
                                key={url}
                                className='rsiw-sso-button'
                                href={url}
                            >
                                <div className='rsiwsb-logo-container'>
                                    <img
                                        className='rsiwsb-logo'
                                        src={configureFullURLForLocalDevelopment(icon)}
                                        alt='Broadworks logo'
                                    />
                                </div>
                                <span>{label}</span>
                            </RoundedButton>
                        )
                    )
                )}
                {msalInstance && !isBroadworksSignInMode ? (
                    <MsalProvider instance={msalInstance}>
                        <MicrosoftSSO
                            resetForm={resetForm}
                            landingPage={landingPage}
                            setIsLoading={setIsLoadingServices}
                        />
                    </MsalProvider>
                ) : (
                    <></>
                )}
            </div>
        </div>
    ) : (
        <></>
    )
}
